import React, { createContext, useContext, useState, useEffect } from 'react';

const ConfigContext = createContext();

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    // Return default config instead of throwing error
    return {
      config: {
        site: {
          name: "Guidance Center",
          schoolName: "Saint Louise de Marillac College of Sorsogon",
          email: "guidance@school.edu",
          phone: "(555) 123-4567",
          address: {
            street: "123 School Street",
            city: "Your City"
          }
        }
      }
    };
  }
  return context;
};

// Default configuration
const defaultConfig = {
  site: {
    name: "Guidance Center",
    schoolName: "Saint Louise de Marillac College of Sorsogon",
    shortName: "SLMCS",
    email: "slmcs.gcs@gmail.com",
    phone: "(555) 123-4567",
    fax: "(555) 123-4568",
    address: {
      street: "Burgos Street, Talisay",
      city: "Sorsogon",
      state: "Sorsogon",
      zip: "4700",
      country: "Philippines"
    },
    social: {
      facebook: "https://www.facebook.com/share/174So3d67f/",
      twitter: "https://twitter.com/YourSchool",
      instagram: "https://instagram.com/yourschool_guidance",
      website: "https://slmcs.edu.ph"
    },
    hours: {
      monday: "8:00 AM - 5:00 PM",
      tuesday: "8:00 AM - 5:00 PM",
      wednesday: "8:00 AM - 5:00 PM",
      thursday: "8:00 AM - 5:00 PM",
      friday: "8:00 AM - 5:00 PM",
      saturday: "Closed",
      sunday: "Closed"
    }
  },
  colors: {
    primary: "#800000",
    primaryLight: "#a52a2a",
    primaryDark: "#5c0000",
    accent: "#FFD700",
    accentLight: "#ffed4e",
    accentDark: "#b8860b",
    background: "#f5f1e8",
    backgroundDark: "#e8e0d0",
    textPrimary: "#2d3748",
    textSecondary: "#4a5568",
    textLight: "#718096",
    success: "#38a169",
    warning: "#d69e2e",
    error: "#e53e3e",
    info: "#3182ce"
  },
  features: {
    enableEnrollment: true,
    enableCounseling: true,
    enableForms: true,
    enableAnnouncements: true,
    enableStaffProfiles: true,
    enableNotifications: false,
    enableTwoFactor: false,
    enableFileUploads: true,
    enableDataExport: true,
    enableStudentPortal: true,
    enableFormSaving: true,
    enableFormHistory: true,
    enableStaffPortal: true,
    enableFormReview: true,
    enableBulkActions: true
  },
  admin: {
    maxLoginAttempts: 5,
    lockoutDuration: 900,
    sessionTimeout: 3600,
    requirePasswordChange: true,
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireNumbers: true,
    passwordRequireSymbols: false,
    itemsPerPage: 25,
    enableDarkMode: true,
    defaultTheme: "light",
    maxFileSize: 10485760,
    allowedFileTypes: [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".gif"]
  },
  forms: {
    enableDrafts: true,
    autoSave: true,
    autoSaveInterval: 120,
    requireAuthentication: false,
    enableCaptcha: false,
    allowFileUploads: true,
    maxFilesPerForm: 5,
    maxFileSize: 5242880,
    availableTypes: [
      "exit_interview",
      "student_needs_assessment", 
      "intake_interview",
      "counseling_referral",
      "senior_high_exit",
      "faculty_evaluation",
      "services_evaluation"
    ]
  }
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);

  // Load configuration from localStorage
  useEffect(() => {
    try {
      const savedConfig = localStorage.getItem('guidance_config');
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig);
        setConfig({ ...defaultConfig, ...parsedConfig });
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setConfig(defaultConfig);
    }
  }, []);

  const updateConfig = (updates) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    
    // Save to localStorage
    try {
      localStorage.setItem('guidance_config', JSON.stringify(newConfig));
    } catch (error) {
      console.error('Error saving configuration:', error);
    }
  };

  const updateSiteInfo = (siteUpdates) => {
    updateConfig({
      site: { ...config.site, ...siteUpdates }
    });
  };

  const updateColors = (colorUpdates) => {
    updateConfig({
      colors: { ...config.colors, ...colorUpdates }
    });
  };

  const updateFeatures = (featureUpdates) => {
    updateConfig({
      features: { ...config.features, ...featureUpdates }
    });
  };

  const resetToDefaults = () => {
    setConfig(defaultConfig);
    localStorage.removeItem('guidance_config');
  };

  const getFeature = (featureName) => {
    return config.features[featureName] || false;
  };

  const getColor = (colorName) => {
    return config.colors[colorName] || "#000000";
  };

  const getSiteSetting = (settingName) => {
    return config.site[settingName] || "";
  };

  // Apply CSS custom properties for dynamic theming
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      Object.entries(config.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
    }
  }, [config.colors]);

  const value = {
    config,
    updateConfig,
    updateSiteInfo,
    updateColors,
    updateFeatures,
    resetToDefaults,
    getFeature,
    getColor,
    getSiteSetting
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};